import { useQuery } from "@vue/apollo-composable";
import type { LocationQueryRaw } from "vue-router";
import { TenancyFilterBookmarksDocument } from "~/graphql/generated/graphql";
import { getTenancyDefaultUrlQuery } from "~/helpers/apollo/apollo-portfolio-filter-helpers";
import { isStandardSetting, settingsTemplateOptions, settingsTemplateOptionsTranslationKeys } from "~/helpers/modules/opex-helpers";

export enum MenuItemType {
  HOME,
  SEARCH,
  ASSETS,
  RENT_ROLL,
  ANALYTICS,
  OPEX_TRACKER,
  ACCOUNTS,
  SETTINGS,
  DATA_MANAGEMENT,
  DATA_MAPPING,
  INTEGRATION_HUB,
  PIPELINES,
}

export type SideMenuItem = {
  type: MenuItemType;
  iconId?: string;
  position: number;
  labelText: string;
  active: ComputedRef<boolean>;
  children?: SideMenuItem[];
  route?: { name: string; query?: LocationQueryRaw };
  onClick?: () => void;
};

export const useChromeMenuItems = () => {
  const route = useRoute();
  const t = useTranslator();
  const filter = useFilter();
  const isFeatureSupported = useIsFeatureSupported();
  const filterBookmarksQuery = useQuery(TenancyFilterBookmarksDocument);
  const opexPreferencesQuery = usePreferencesNamespace("preferencesNamespaceOpex");
  const opexBookmarksQuery = useOpexSavedTemplates(true);
  const ampli = useAmplitude();

  const home = Array<SideMenuItem>(
    {
      type: MenuItemType.SEARCH,
      iconId: "search",
      position: 0,
      labelText: t("SIDE_MENU_ITEM_SEARCH"),
      children: undefined,
      active: computed(() => false), // Open search in a modal
      route: undefined,
    },
    {
      type: MenuItemType.HOME,
      iconId: "home",
      position: 1,
      labelText: t("SIDE_MENU_ITEM_HOME"),
      children: undefined,
      active: computed(() => route.path === "/"),
      route: { name: "index", query: undefined },
    },
    {
      type: MenuItemType.ASSETS,
      iconId: "location_city",
      position: 2,
      labelText: t("SIDE_MENU_ITEM_ASSETS"),
      children: undefined,
      active: computed(() => route.path.startsWith("/assets")),
      route: { name: "assets", query: undefined },
    }
  );

  const modules = Array<SideMenuItem>(
    {
      type: MenuItemType.ANALYTICS,
      iconId: "area_chart",
      position: 0,
      labelText: t("SIDE_MENU_ITEM_ANALYTICS"),
      children: undefined,
      active: computed(() => route.path.startsWith("/analytics")),
      route: { name: "analytics", query: undefined },
    },
    {
      type: MenuItemType.ACCOUNTS,
      iconId: "account_balance",
      position: 3,
      labelText: t("SIDE_MENU_ITEM_ACCOUNTS"),
      children: undefined,
      active: computed(() => route.path.startsWith("/accounts")),
      route: { name: "accounts", query: undefined },
    }
  );

  const settings = Array<SideMenuItem>({
    type: MenuItemType.SETTINGS,
    iconId: "manage_accounts",
    position: 1,
    labelText: t("SIDE_MENU_ITEM_PERSONAL_SETTINGS"),
    children: undefined,
    active: computed(() => route.path.startsWith("/settings")),
    route: { name: "settings", query: undefined },
  });

  const filterBookmarksLoading = computed(() => filterBookmarksQuery.loading.value);
  const filterBookmarks = computed(() => filterBookmarksQuery.result.value?.assetManagementTenancyFilterBookmarks);
  const defaultFilter = computed(() => filterBookmarksQuery.result.value?.assetManagementTenancyFilterBookmarks?.find((f) => f.isDefault));

  const opexBookmarks = computed(() => opexBookmarksQuery.result.value);
  const opexBookmarksLoading = computed(() => opexBookmarksQuery.loading.value);

  const loading = computed(() => filterBookmarksLoading.value || opexBookmarksLoading.value);
  const settingsItems = [
    ...(isFeatureSupported("portfolioDataWarehouse")
      ? [
          {
            type: MenuItemType.DATA_MANAGEMENT,
            iconId: "icon-database",
            position: 0,
            labelText: t("SIDE_MENU_ITEM_DATA_WAREHOUSE"),
            active: computed(() => route.path == "/datamanagement"),
            route: undefined,
            children: [
              {
                type: MenuItemType.DATA_MAPPING,
                iconId: "category",
                position: 0,
                labelText: t("SIDE_MENU_ITEM_DATA_MAPPING"),
                active: computed(() => route.path.startsWith("/datamanagement/datamapping")),
                route: { name: "datamanagement-datamapping", query: undefined },
                children: undefined,
              },

              ...(useHasPermissions(PERMISSIONS.INTEGRATION_READ)
                ? [
                    {
                      type: MenuItemType.INTEGRATION_HUB,
                      iconId: "hub",
                      position: 1,
                      labelText: t("SIDE_MENU_ITEM_INTEGRATION_HUB"),
                      active: computed(() => route.path.startsWith("/datamanagement/integrationhub")),
                      route: { name: "datamanagement-integrationhub", query: undefined },
                      children: undefined,
                    },
                  ]
                : []),

              {
                type: MenuItemType.PIPELINES,
                iconId: "graph_1",
                position: 2,
                labelText: t("SIDE_MENU_ITEM_PIPELINES"),
                active: computed(() => route.path.startsWith("/datamanagement/pipelines")),
                route: { name: "datamanagement-pipelines", query: undefined },
                children: undefined,
              },
            ],
          },
        ]
      : []),
    ...settings,
  ];

  const moduleItems = computed<SideMenuItem[]>(() => {
    const m: SideMenuItem[] = [
      {
        type: MenuItemType.RENT_ROLL,
        iconId: "table_view",
        position: 1,
        labelText: t("SIDE_MENU_ITEM_RENT_ROLL"),
        active: computed(() => route.path.startsWith("/rentroll") && route.query.filterId === defaultFilter.value?.id),
        route: {
          name: `rentroll`,
          query: { ...getTenancyDefaultUrlQuery(defaultFilter.value?.id), reload: defaultFilter.value ? "true" : undefined },
        },
        onClick: () => {
          if (defaultFilter.value)
            ampli.openSavedRentRoll({
              savedFilterId: defaultFilter.value.id,
              savedFilterName: defaultFilter.value.name,
            });
        },
        children: filterBookmarks.value?.map((bookmark, i) => {
          return {
            type: MenuItemType.RENT_ROLL,
            position: i,
            iconId: undefined,
            labelText: filter.limitCharacters(bookmark.name, 24),
            active: computed(() => route.path.startsWith("/rentroll") && route.query.filterId === bookmark.id),
            route: { name: "rentroll", query: { ...route.query, filterId: bookmark.id, reload: "true" } as LocationQueryRaw },
            onClick: () =>
              ampli.openSavedRentRoll({
                savedFilterId: bookmark.id,
                savedFilterName: bookmark.name,
              }),
          };
        }),
      },
      {
        type: MenuItemType.OPEX_TRACKER,
        iconId: "rubric",
        position: 2,
        labelText: t("SIDE_MENU_ITEM_OPEX"),
        active: computed(() => route.path.startsWith("/opextracker")),
        route: { name: "opextracker", query: { templateId: opexPreferencesQuery.result.value?.template } },
        children: [...(opexBookmarks.value ?? [])].map((bookmark, i) => {
          return {
            type: MenuItemType.OPEX_TRACKER,
            position: i,
            iconId: undefined,
            labelText: filter.limitCharacters(isStandardSetting(bookmark) ? t(settingsTemplateOptionsTranslationKeys[bookmark.id]) : bookmark.name, 24),
            active: computed(() => route.path.startsWith("/opextracker") && route.query.templateId === bookmark.id),
            route: { name: "opextracker", query: { ...route.query, templateId: bookmark.id } as LocationQueryRaw },
            params: { templateId: bookmark.id },
          };
        }),
      },
      ...modules,
    ];

    return m;
  });

  return {
    loading: loading,
    homeItems: home,
    moduleItems: moduleItems,
    settingsItems: settingsItems,
  };
};
